<template>
  <ol
    class="report-timeline__wrap"
    :class="timelineClass"
    aria-label="처리 단계"
  >
    <li
      v-for="(step, index) in timelineList"
      :key="index"
      class="report-timeline__step"
      :class="{
        'is-done': index < currentStepIndex,
        'is-current':
          index === currentStepIndex ||
          (showDualProgress && index === currentStepIndex + 1),
        'is-complete':
          index > currentStepIndex &&
          !(showDualProgress && index === currentStepIndex + 1),
      }"
      :aria-current="index === currentStepIndex ? 'step' : null"
    >
      <span class="report-timeline__dot" aria-hidden="true"></span>
      <span
        v-if="index !== timelineList.length"
        class="report-timeline__bar"
        aria-hidden="true"
      ></span>
      <span class="report-timeline__label">{{ step.label }}</span>
      <span class="report-timeline__sub">
        {{
          index === currentStepIndex ||
          (showDualProgress && index === currentStepIndex + 1)
            ? "진행 중"
            : step.formatted
        }}
      </span>
    </li>
    <li v-if="!reportComplete" class="report-timeline__step is-complete">
      <span class="report-timeline__dot" aria-hidden="true"></span>
      <span class="report-timeline__label">종료</span>
      <span class="report-timeline__sub" aria-label="진행 전">진행 전</span>
    </li>
    <li v-if="reportComplete" class="report-timeline__step is-done">
      <span class="report-timeline__dot" aria-hidden="true"></span>
      <span class="report-timeline__label">종료</span>
      <span class="report-timeline__sub" aria-label="종료">{{
        actionStatus ? formatDate(reportCompleteDate) : "-"
      }}</span>
    </li>
  </ol>
</template>

<script>
import { ref, watch } from "vue";

export default {
  name: "ReportTimeline",
  props: {
    timelineClass: {
      type: [String, Array, Object],
      default: "",
    },
    timelineTooltipData: {
      type: Array,
      required: true,
    },
  },
  setup(props) {
    // data 정의
    const timelineList = ref([]);
    const currentStepIndex = ref(0);
    const reportComplete = ref(""); // 신고 접수 완료 여부
    const reportCompleteDate = ref(""); // 신고 접수 완료 날짜
    const showDualProgress = ref(false); // 평가중, 분석중 여부
    const actionStatus = ref(false); // 조치 대상 여부

    // methods 정의
    const formatDate = (dateStr) => {
      if (!dateStr) {
        return "";
      }

      const date = new Date(dateStr);

      if (isNaN(date.getTime())) {
        return "";
      }

      const yy = String(date.getFullYear()).slice(2);
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");

      return `${yy}.${mm}.${dd}`;
    };
    const convertData = (data) => {
      const item = data[0];
      if (!item) {
        timelineList.value = [];
        return;
      }

      actionStatus.value = item.actionStatus ?? false; // 조치 대상 여부
      const evlStatus = item.evlTarget ?? false; // 평가 대상 여부

      let evlComplete = false;
      if (evlStatus && item.confirmDate) {
        evlComplete = true;
      }

      // 평가 완료 여부 및 완료 일자
      if (!actionStatus.value && item.actionDate) {
        reportComplete.value = true;
        reportCompleteDate.value = item.actionDate;
      } else if (!evlStatus && item.analEvlDate) {
        reportComplete.value = true;
        reportCompleteDate.value = item.analEvlDate;
      } else if (evlComplete) {
        reportComplete.value = true;
        reportCompleteDate.value = item.confirmDate;
      }

      const fields = [
        { key: "regDate", label: "등록" },
        { key: "distDate", label: "분석" },
        { key: "analDate", label: "분석" },
        { key: "actionDate", label: "조치" },
        { key: "replyRegDate", label: "패치" },
        { key: "replyDate", label: "패치" },
        { key: "analEvlDate", label: "평가" },
        { key: "hisRegDate", label: "평가" },
        { key: "finalsEvlDate", label: "최종 평가" },
      ];

      const stepArr = fields.map((f) => {
        let rawDate = item[f.key];

        // 조치 미대상인 경우 조치 이후 종료로 표현
        if (
          (f.key === "replyRegDate" ||
            f.key === "replyDate" ||
            f.key === "analEvlDate" ||
            f.key === "hisRegDate" ||
            f.key === "finalsEvlDate") &&
          !actionStatus.value
        ) {
          rawDate = null;
        }

        if (f.key === "replyRegDate" && actionStatus.value) {
          rawDate = item.patchDate;
        }
        if (f.key === "hisRegDate" && actionStatus.value && evlComplete) {
          rawDate = item.confirmDate;
        }
        if (f.key === "distDate" && item.analDate) {
          rawDate = null;
        }
        if (f.key === "replyRegDate" && item.replyDate) {
          rawDate = null;
        }
        if (f.key === "analEvlDate" && (item.hisRegDate || item.confirmDate)) {
          rawDate = null;
        }

        return {
          key: f.key,
          label: f.label,
          date: rawDate,
          formatted: formatDate(rawDate),
        };
      });

      const sortedSteps = stepArr
        .filter((s) => s.date)
        .sort((a, b) => {
          if (a.key === "regDate") return -1;
          if (b.key === "regDate") return 1;

          // 분석일이 조치일 이후인 경우 분석->조치 순서 고정
          const analysisKeys = ["distDate", "analDate"];
          const actionKeys = ["actionDate"];

          if (analysisKeys.includes(a.key) && actionKeys.includes(b.key)) {
            return -1;
          }
          if (actionKeys.includes(a.key) && analysisKeys.includes(b.key)) {
            return 1;
          }

          // 평가완료, 분석중 이면 분석을 맨 뒤로
          if (
            item.finalsEvlDate &&
            !item.confirmDate &&
            item.analEvlDate &&
            !item.analDate
          ) {
            if (a.key === "distDate") return 1;
            if (b.key === "distDate") return -1;
          }

          // 평가완료, 조치중 이면 조치를 맨 뒤로
          if (
            item.finalsEvlDate &&
            !item.confirmDate &&
            item.actionDate &&
            !item.replyDate
          ) {
            if (a.key === "actionDate") return 1;
            if (b.key === "actionDate") return -1;
          }

          // 평가완료, 패치중 이면 패치를 맨 뒤로
          if (item.finalsEvlDate && !item.confirmDate && item.replyDate) {
            if (a.key === "replyDate") return 1;
            if (b.key === "replyDate") return -1;
          }

          // 최종평가는 평가 이후
          if (a.key === "finalsEvlDate") return 1;
          if (b.key === "finalsEvlDate") return -1;

          return new Date(a.date) - new Date(b.date);
        });

      timelineList.value = sortedSteps;

      if (reportComplete.value) {
        currentStepIndex.value = sortedSteps.length;
        showDualProgress.value = false;
      } else {
        // 평가중, 분석중 일 경우 둘 다 진행 중으로 표시
        if (!item.finalsEvlDate && item.analEvlDate && !item.analDate) {
          currentStepIndex.value =
            sortedSteps.length >= 2
              ? sortedSteps.length - 2
              : sortedSteps.length - 1;
          showDualProgress.value = true;
        } else {
          currentStepIndex.value = sortedSteps.length
            ? sortedSteps.length - 1
            : 0;
          showDualProgress.value = false;
        }
      }
    };

    // watch 정의
    watch(
      () => props.timelineTooltipData,
      (val) => convertData(val),
      { immediate: true }
    );

    // html에서 사용될 데이터 정의
    return {
      timelineList,
      currentStepIndex,
      timelineClass: props.timelineClass,
      reportComplete,
      formatDate,
      reportCompleteDate,
      showDualProgress,
      actionStatus,
    };
  },
};
</script>

<style scoped>
</style>
